<template>
  <div class="result-wrap">
    <div class="card-results" :class="{ 'has-flip': hasFlip, flip: isFlipped }">
      <template v-if="$route.path.includes('/results/')">
        <TestCard
          :test-score="testScore"
        />
      </template>
      <template v-else>
        <template v-if="status === 'in-progress' || guestReview">
          <StudyCard
            v-if="mainScore.score || mainScore.score === 0"
            :study-score="mainScore"
            :can-rotate="hasFlip"
            :review-progress="!mainScore.knownCount"
            @rotate="isFlipped = !isFlipped"
          />
          <TestCard
            v-if="testScore.totalAnswers"
            :test-score="testScore"
            :can-rotate="hasFlip"
            @rotate="isFlipped = !isFlipped"
          />
        </template>
        <template v-if="status === 'completed'">
          <TestCard
            v-if="testScore.totalAnswers"
            :test-score="testScore"
            :can-rotate="hasFlip"
            @rotate="isFlipped = !isFlipped"
          />
          <StudyCard
            v-if="mainScore.score || mainScore.score === 0"
            :study-score="mainScore"
            :can-rotate="hasFlip"
            @rotate="isFlipped = !isFlipped"
          />
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import cookies from 'js-cookie';
import TestCard from './Test';
import StudyCard from './Study';

export default {
  components: {
    TestCard,
    StudyCard,
  },
  props: {
    testScore: { type: Object, required: true, default: () => ({}) },
    studyScore: { type: Object, required: false, default: () => ({}) },
    status: { type: String, required: false, default: null },
    deck: { type: Object, required: false, default: () => ({}) },
  },
  data() {
    return {
      mainScore: this.studyScore,
      isFlipped: false,
    };
  },
  computed: {
    guestReview() {
      return this.$route.query.review && !this.$store.state.auth.user;
    },
    hasFlip() {
      return !!(this.testScore.totalAnswers && (this.mainScore.score || this.mainScore.score === 0));
    },
  },
  mounted() {
    if (!this.$store.state.auth.user) {
      const review = cookies.get('review_cards');
      let reviewed = 0;
      if (review) {
        const data = JSON.parse(review);
        if (data[this.deck.id]) {
          reviewed = data[this.deck.id];
        }
      }

      if (reviewed) {
        this.mainScore = {
          reviewedCount: reviewed,
          notReviewedCount: this.deck.cardCount - reviewed,
          score: Math.round((reviewed / this.deck.cardCount) * 100),
        };
      }
    }
  },
};
</script>
<style lang="scss">
.result-wrap {
  perspective: 1000px;
}

.card-results {
  &.has-flip {
    height: 350px;
    position: relative;
    transition: transform 0.25s;
    transform-style: preserve-3d;

    @-moz-document url-prefix() {
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;
    }

    &.flip {
      transform: rotateX(180deg);
      transition: transform 0.25s;
    }

    .exam-result,
    .deck-progress {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      -webkit-backface-visibility: hidden;
      backface-visibility: hidden;

      &:first-child {
        display: block;
        overflow: hidden;
        z-index: 1;
      }

      &:nth-child(2) {
        z-index: 0;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        transform: rotateX(180deg);
      }
    }
  }

  .deck-progress {
    background: var(--main-light);
    border: 1px solid var(--accent);

    .holder-result {
      .title {
        margin-top: 0;
        font-size: 24px;
        font-weight: 500;
        line-height: 110%;
        margin-bottom: var(--spacing-40);
      }

      .result {
        margin-right: 0;
      }
    }
  }

  .exam-result,
  .deck-progress {
    padding: var(--spacing-30) var(--spacing-50);
    border-radius: 10px;

    .holder-result {
      display: flex;
      justify-content: space-between;

      .result {
        max-width: 360px;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin-right: var(--spacing-30);
      }

      .answers {
        margin-right: 70px;

        &.mobile {
          display: none;
        }

        div {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-16);

          &:last-child {
            margin-bottom: 0;
          }

          span {
            font-size: 18px;
            font-weight: 500;
            line-height: 140%;
          }
        }

        .correct {
          background: #55BFC7;
        }

        .wrong {
          background: var(--main-red);
        }

        .skipped {
          background: var(--main-light-shade-4);
        }

        .correct,
        .wrong,
        .skipped {
          font-size: 16px;
          border-radius: 8px;
          padding: var(--spacing-4) var(--spacing-16);
          min-width: 54px;
          text-align: center;
        }
      }
    }

    .btn-holder {
      display: flex;
      justify-content: flex-end;
      margin-top: var(--spacing-18);

      .btn-text {
        padding: 0;
        color: var(--main-light-shade-4);
      }
    }
  }

  .exam-result {
    background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), #000C1F;

    .holder-result {
      color: var(--main-light);

      .your-result {
        margin-top: 0;
        font-size: 24px;
        font-weight: 500;
        line-height: 110%;
        margin-bottom: var(--spacing-30);
      }

      .title {
        margin-top: 0;
        margin-bottom: var(--spacing-6);
      }

      .subtitle {
        font-size: 12px;
        font-weight: 400;
        line-height: 110%;
        color: var(--main-light-shade-4);
        margin: 0 0 var(--spacing-40);
      }

      .progress {
        outline: 3px solid rgba(20, 49, 78, 1);
        background: rgba(20, 49, 78, 1);

        &:after {
          content: '';
          background: linear-gradient(135deg, rgba(86, 191, 199, 0.2) 0.94%, rgba(86, 191, 199, 0) 100%), #000C1F;
        }
      }
    }
  }

  .progress {
    width: 255px;
    outline: 3px solid rgba(20, 31, 49, 0.12);
    background: rgba(20, 31, 49, 0.12);
    border-radius: 50%;
    position: relative;
    z-index: 0;

    canvas {
      width: 255px;
    }

    &:after {
      content: '';
      background: var(--main-light);
      border-radius: 50%;
      position: absolute;
      width: 172px;
      height: 172px;
      top: 41px;
      left: 41px;
    }

    .number {
      position: absolute;
      z-index: 1;
      text-align: center;
      top: 86px;
      width: 100%;

      p {
        margin: 0;
        font-size: 18px;
        font-weight: 500;
      }

      p:first-of-type {
        font-size: 40px;
        margin-bottom: var(--spacing-8);
      }
    }
  }
}

@include bp($bp-laptop) {
  .card-results {
    .exam-result,
    .deck-progress {
      .holder-result {
        .progress-holder {
          height: 240px;

          .progress {
            width: 240px;

            canvas {
              width: 240px;
            }

            &:after {
              width: 173px;
              height: 173px;
              top: 34px;
              left: 34px;
            }
          }

          .number {
            top: 76px;
          }
        }

        .answers {
          margin-right: var(--spacing-40);
        }
      }

      .btn-holder {
        margin-top: var(--spacing-24);
      }
    }
  }
}

@include bp($bp-mobile) {
  .card-results {
    &.has-flip {
      height: 500px;

      .exam-result,
      .deck-progress {
        &:nth-child(2) {
          transform: rotateY(180deg);
        }
      }

      &.flip {
        transform: rotateY(180deg);
      }
    }

    .exam-result,
    .deck-progress {
      padding: var(--spacing-40) var(--spacing-20) var(--spacing-30);

      .holder-result {
        flex-direction: column;

        .result {
          max-width: unset;
          margin-right: 0;

          .your-result {
            margin-bottom: var(--spacing-12);
          }
        }

        .progress-holder {
          display: flex;
          justify-content: center;
          margin-bottom: var(--spacing-22);

          .progress {
            width: 200px;

            canvas {
              width: 200px;
            }

            &:after {
              width: 133px;
              height: 134px;
              top: 33px;
              left: 34px;
            }
          }

          .number {
            top: 60px;
          }
        }

        .answers {
          margin-right: 0;

          div:last-child {
            margin-bottom: var(--spacing-16);
          }

          &.mobile {
            display: flex;
            justify-content: center;

            .correct,
            .wrong,
            .skipped {
              padding: 0 var(--spacing-4);
              min-width: 40px;
              height: 22px;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-top: var(--spacing-10);
            }

            div {
              flex-direction: column;
              align-items: center;
            }
          }

          &.desktop {
            display: none;
          }
        }
      }
    }

    .exam-result {
      .holder-result {
        .answers.mobile {
          column-gap: 30px;

          div {
            width: 70px;
            text-align: center;
            margin-bottom: var(--spacing-16);

            span {
              font-size: 16px;
            }
          }
        }

        .result {
          .title {
            font-size: 14px;
          }
        }
      }
    }

    .deck-progress {
      .holder-result {
        .answers.mobile {
          column-gap: 10px;

          div {
            text-align: center;
            width: 90px;

            span {
              font-size: 14px;
            }
          }
        }
      }
    }
  }
}
</style>
